/**
 * Géocodage Photon — même moteur que l'app client et que la console.
 *
 * Photon (komoot) est open source, adossé aux données OpenStreetMap, et ne
 * demande ni clé ni abonnement. Il remplace les appels directs à
 * `api.mapbox.com` que faisait le web : ceux-ci exposaient un jeton Mapbox dans
 * l'URL de chaque frappe au clavier, étaient facturés à la requête, et
 * divergeaient des résultats que l'app mobile montre pour la même recherche.
 *
 * `NEXT_PUBLIC_PHOTON_URL` permet de pointer une instance auto-hébergée sans
 * toucher aux écrans ; par défaut on utilise l'instance publique, dont les
 * auteurs demandent un usage raisonnable (« extensive usage will be throttled »).
 *
 * Porté depuis `standalone/console-web-front/lib/map/geocoding.ts`, sans le
 * secours Google : le web client n'a pas d'intégration Google Maps.
 */

export interface GeocodeResult {
  description: string;
  latitude: number;
  longitude: number;
  name?: string;
  street?: string;
  housenumber?: string;
  city?: string;
  district?: string;
  postcode?: string;
  country?: string;
  countryCode?: string;
  /** Identifiant OSM quand disponible (`osm:N123456`). */
  id?: string;
}

export interface SearchOptions {
  near?: { latitude: number; longitude: number };
  limit?: number;
  /** Pays (ISO-2) ; `null` = monde entier. Défaut : DZ. */
  countryCode?: string | null;
  signal?: AbortSignal;
}

const PHOTON = (process.env.NEXT_PUBLIC_PHOTON_URL ?? "https://photon.komoot.io").replace(
  /\/+$/,
  "",
);
const TIMEOUT_MS = 6000;

/** Langues servies par Photon ; tout le reste (dont l'arabe) retombe sur le français. */
const PHOTON_LANGS = new Set(["fr", "en", "de", "it", "es", "nl"]);

function lang(preferred?: string): string {
  const raw =
    preferred ??
    (typeof navigator !== "undefined" ? navigator.language : undefined) ??
    "fr";
  const code = raw.slice(0, 2).toLowerCase();
  return PHOTON_LANGS.has(code) ? code : "fr";
}

type PhotonFeature = {
  geometry?: { coordinates?: [number, number] };
  properties?: Record<string, string | number | undefined>;
};

/** « 12 Rue Didouche Mourad, Alger, Algérie » à partir des propriétés OSM. */
function describe(p: Record<string, string | number | undefined>): string {
  const first = [p.housenumber, p.street ?? p.name].filter(Boolean).join(" ");
  const parts = [first, p.district, p.city ?? p.town ?? p.village, p.country].filter(
    (v, i, arr) => v && arr.indexOf(v) === i,
  );
  return parts.join(", ");
}

function normalize(f: PhotonFeature): GeocodeResult | null {
  const c = f.geometry?.coordinates;
  const p = f.properties ?? {};
  if (!c || c.length !== 2) return null;
  return {
    description: describe(p),
    longitude: c[0],
    latitude: c[1],
    name: p.name as string | undefined,
    street: p.street as string | undefined,
    housenumber: p.housenumber as string | undefined,
    city: (p.city ?? p.town ?? p.village) as string | undefined,
    district: p.district as string | undefined,
    postcode: p.postcode as string | undefined,
    country: p.country as string | undefined,
    countryCode: (p.countrycode as string | undefined)?.toUpperCase(),
    id: p.osm_id != null ? `osm:${p.osm_type ?? ""}${p.osm_id}` : undefined,
  };
}

async function fetchOnce(url: string, signal?: AbortSignal): Promise<Response | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  signal?.addEventListener("abort", () => controller.abort(), { once: true });
  try {
    return await fetch(url, {
      signal: controller.signal,
      headers: { Accept: "application/json" },
    });
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Une seule reprise sur échec transitoire (429 / 5xx / réseau).
 *
 * Sans reprise, un échec isolé coûte l'adresse : « utiliser ma position »
 * retombe sur le libellé générique « Position actuelle », et pour une personne
 * connectée une adresse est enregistrée avec ce libellé-là. L'instance publique
 * annonce elle-même un throttling au-delà d'un usage raisonnable.
 *
 * Écart assumé avec la console et l'app mobile, qui n'en font pas : ici la
 * personne est en train de commander, et la reprise ne coûte rien quand le
 * service répond.
 *
 * À savoir en test : `photon.komoot.io/reverse` répond 503 à une requête sans
 * `User-Agent` — ce que fait `fetch` sous Node, pas un navigateur. Un script de
 * vérification doit donc en poser un, sinon il conclut à tort à une panne.
 */
async function getFeatures(url: string, signal?: AbortSignal): Promise<PhotonFeature[]> {
  for (let attempt = 0; attempt < 2; attempt++) {
    if (signal?.aborted) return [];
    const res = await fetchOnce(url, signal);

    if (res?.ok) {
      try {
        const data = (await res.json()) as { features?: PhotonFeature[] };
        return data.features ?? [];
      } catch {
        return [];
      }
    }

    // 4xx autre que 429 : la requête est mauvaise, réessayer n'y changera rien.
    if (res && res.status !== 429 && res.status < 500) return [];
    if (attempt === 0) await new Promise((r) => setTimeout(r, 350));
  }
  return [];
}

/**
 * Autocomplétion d'adresse. `near` biaise vers la position de la personne,
 * `countryCode` filtre les résultats (l'Algérie par défaut).
 */
export async function searchAddress(
  query: string,
  options: SearchOptions & { language?: string } = {},
): Promise<GeocodeResult[]> {
  const q = query.trim();
  if (q.length < 2) return [];

  const { near, limit = 6, countryCode = "DZ", signal, language } = options;
  const params = new URLSearchParams({
    q,
    lang: lang(language),
    // On demande large : le filtre pays ci-dessous en écarte une partie.
    limit: String(limit * 2),
  });
  if (near) {
    params.set("lat", String(near.latitude));
    params.set("lon", String(near.longitude));
    params.set("location_bias_scale", "0.4");
  }

  const features = await getFeatures(`${PHOTON}/api/?${params.toString()}`, signal);
  const out: GeocodeResult[] = [];
  const seen = new Set<string>();
  for (const f of features) {
    const r = normalize(f);
    if (!r || !r.description) continue;
    if (countryCode && r.countryCode && r.countryCode !== countryCode.toUpperCase()) continue;
    if (seen.has(r.description)) continue;
    seen.add(r.description);
    out.push(r);
    if (out.length >= limit) break;
  }
  return out;
}

/** Adresse lisible à partir d'une position, ou `null`. */
export async function reverseGeocode(
  coords: { latitude: number; longitude: number },
  options: { language?: string; signal?: AbortSignal } = {},
): Promise<GeocodeResult | null> {
  const params = new URLSearchParams({
    lat: String(coords.latitude),
    lon: String(coords.longitude),
    lang: lang(options.language),
    limit: "1",
  });
  const [first] = await getFeatures(`${PHOTON}/reverse?${params.toString()}`, options.signal);
  return first ? normalize(first) : null;
}
